import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@utils/cn';

const badgeVariantsClasses = cva(
  cn(
    'inline-flex items-center gap-1 border h-fit w-fit transition-colors',
    'font-medium',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  ),
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border bg-secondary text-secondary-foreground',
        muted: 'border bg-muted text-secondary-foreground', // use darker text than text-muted-foreground
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        success: 'border-transparent bg-emerald-600 text-emerald-50 hover:bg-emerald-600/80',
        warning: 'border-transparent bg-amber-600 text-amber-50 hover:bg-amber-600/80',
        info: 'border-transparent bg-sky-600 text-sky-50 hover:bg-sky-600/80',
        ai: 'border-transparent bg-purple-600 text-purple-50 hover:bg-purple-600/80',
        outline: 'bg-background border text-foreground',
        'success-outline': 'bg-emerald-100 border-emerald-500 text-emerald-700',
        'destructive-outline': 'bg-destructive/10 border border-destructive/20 text-destructive',
        'warning-outline': 'bg-amber-100 border border-amber-500 text-amber-700',
        'info-outline': 'bg-sky-100 border border-sky-500 text-sky-700',
        'ai-outline': 'bg-purple-100 border border-purple-500 text-purple-700',
        inverted: 'border-transparent bg-background text-primary',
      },
      shape: {
        rounded: 'rounded-lg',
      },
      size: {
        default: 'px-1.5 py-0.5 text-xs',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'secondary',
      shape: 'rounded',
    },
  },
);
export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariantsClasses> {
  asChild?: boolean;
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, shape, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';

    return (
      <Comp
        ref={ref}
        className={cn(badgeVariantsClasses({ variant, shape, size }), className)}
        {...props}
      />
    );
  },
);
Badge.displayName = 'Badge';
export { Badge, badgeVariantsClasses as badgeVariants };
